import { FileText, File, FileVideo, FileAudio, FileCode, FileSpreadsheet } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { cn } from "@/modern/utils";
import PdfViewer, { PdfViewerProps } from "./pdf-viewer";
import WordViewer, { WordViewerProps } from "./word-viewer";
import { getPresignedImageUrlForFile, getPresignedImageUrlForUnassignedFile } from "@/network/network";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { getFileExtension } from "@/modern/utils";
interface FilePreviewProps {
  file: FormattedFile;
  className?: string;
  src?: string;
  showName?: boolean;
  openFileOnClick?: boolean;
  fileIconClassName?: string;
  fileIconContainerClassName?: string;
  disableLoadingState?: boolean; // Disable internal loading animation
  enableVideoAudioRendering?: boolean; // Optional: Enable HTML video/audio tag rendering
  isThumbnail?: boolean; // Whether to render PDF in thumbnail mode
  isSwivuUnAssignedFile?: boolean; // Whether this is an unassigned Swivu file
  isUseFileQueryParam?: boolean;
}

const FilePreview = ({
  file,
  className = "",
  src,
  showName = false,
  openFileOnClick = false,
  fileIconClassName = "",
  fileIconContainerClassName = "",
  disableLoadingState = false,
  enableVideoAudioRendering = false,
  isThumbnail = false,
  isSwivuUnAssignedFile = false,
  isUseFileQueryParam = false,
  ...props
}: FilePreviewProps & React.HTMLAttributes<HTMLDivElement>) => {
  const [isLoading, setIsLoading] = useState(!disableLoadingState);

  const getFileIcon = (filename?: string) => {
    const ext = getFileExtension(filename);
    const iconCls = cn("size-12", fileIconClassName);
    // Image files
    if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "ico"].includes(ext)) {
      return null; // Show actual image
    }

    // Document files
    if (["pdf"].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    if (["doc", "docx", "odt", "rtf", "txt", "md"].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    // Spreadsheet files
    if (["xls", "xlsx", "csv", "ods"].includes(ext)) {
      return <FileSpreadsheet className={iconCls} />;
    }

    // Presentation files
    if (["ppt", "pptx", "odp"].includes(ext)) {
      return <FileText className={iconCls} />;
    }

    // Video files
    if (["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"].includes(ext)) {
      return <FileVideo className={iconCls} />;
    }

    // Audio files
    if (["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"].includes(ext)) {
      return <FileAudio className={iconCls} />;
    }

    // Code files
    if (["js", "jsx", "ts", "tsx", "html", "css", "scss", "json", "xml", "py", "java", "c", "cpp", "h", "cs", "php", "rb", "go", "rs", "swift", "kt"].includes(ext)) {
      return <FileCode className={iconCls} />;
    }

    // Archive files
    if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz"].includes(ext)) {
      return <File className={iconCls} />;
    }

    // Default file icon
    return <File className={iconCls} />;
  };

  // CompanyCam images are always images, regardless of URL extension
  const icon = file.isCCImage ? null : getFileIcon(file.name);
  const isImage = !icon;

  const handleFileClick = () => {
    if (!openFileOnClick) return;
    const fileUrl = file.originalImageUrl || file.thumbnailUrl || src;
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };
  // Check if file is video or audio when rendering is enabled (memoized)
  const { isVideo, isAudio, isPdf, isWordDoc, fileUrl } = useMemo(() => {
    const extension = getFileExtension(file.name);
    const video = enableVideoAudioRendering && ["mp4", "avi", "mov", "wmv", "flv", "mkv", "webm", "m4v"].includes(extension);
    const audio = enableVideoAudioRendering && ["mp3", "wav", "ogg", "m4a", "flac", "aac", "wma"].includes(extension);
    const isPdf = ["pdf"].includes(extension);
    const isWordDoc = ["docx"].includes(extension);
    const url = file.originalImageUrl || src || file.thumbnailUrl;
    return { ext: extension, isVideo: video, isAudio: audio, isPdf, isWordDoc, fileUrl: url };
  }, [file, src, enableVideoAudioRendering]);

  if (isWordDoc && fileUrl && !isThumbnail) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video absolute inset-0 z-10" />}
        <SwivuWordPreviewWrapper
          previewFileObject={file}
          setLoadingState={setIsLoading}
          fileUrl={fileUrl}
          fileName={file.name}
          showName={showName}
          className={className}
          isThumbnail={isThumbnail}
          onLoadSuccess={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onLoadError={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        />
      </>
    );
  }

  if (isPdf && fileUrl) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video absolute inset-0 z-10" />}
        <SwivuPDFPreviewWrapper
          previewFileObject={file}
          setLoadingState={setIsLoading}
          fileUrl={fileUrl}
          fileName={file.name}
          showName={showName}
          className={className}
          isThumbnail={isThumbnail}
          isSwivuUnAssignedFile={isSwivuUnAssignedFile}
          onLoadSuccess={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onLoadError={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        />
      </>
    );
  }

  if (isVideo && fileUrl) {
    return (
      <div className={cn("flex flex-col items-center justify-center", className)} {...props}>
        <video
          src={fileUrl}
          controls
          className="max-h-full max-w-full rounded-md aspect-video object-contain"
          onLoadStart={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        >
          Your browser does not support the video tag.
        </video>
        {showName && file?.name && (
          <span className="text-sm text-center mt-4 px-2 break-words max-w-full">{file.name}</span>
        )}
      </div>
    );
  }

  // Render audio tag if enabled and file is audio
  if (isAudio && fileUrl) {
    return (
      <div className={cn("flex flex-col items-center justify-center", className)} {...props}>
        <audio
          src={fileUrl}
          controls
          className="max-w-full"
          onLoadStart={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
        >
          Your browser does not support the audio tag.
        </audio>
        {showName && file?.name && (
          <span className="text-sm text-center mt-2 px-2 break-words max-w-full">{file.name}</span>
        )}
      </div>
    );
  }

  if (isImage) {
    return (
      <>
        {!disableLoadingState && isLoading && <div className="bg-muted/50 animate-pulse rounded-md border w-full aspect-video" />}
        <img
          src={(src || file.thumbnailUrl || file.originalImageUrl) + (isUseFileQueryParam ? "?v=" + Date.now() : "")}
          alt={file.name}
          className={cn("rounded-md", className, !disableLoadingState && isLoading && " hidden ", "cursor-pointer")}
          onClick={handleFileClick}
          onLoad={() => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
          }}
          onError={(e) => {
            if (!disableLoadingState) {
              setIsLoading(false);
            }
            // On error, replace with file icon
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const iconContainer = document.createElement("div");
            iconContainer.className = cn("w-full h-full flex flex-col items-center justify-center opacity-50 cursor-pointer ", fileIconContainerClassName);
            iconContainer.innerHTML = `<svg class="size-12 text-foreground ${fileIconClassName}" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/></svg>`;
            iconContainer.onclick = handleFileClick;

            if (showName) {
              const nameContainer = document.createElement("div");
              nameContainer.className = cn("text-sm text-center mt-2 px-2 break-words max-w-full");
              nameContainer.innerHTML = file.name || "";
              iconContainer.appendChild(nameContainer);
            }

            target.parentElement?.appendChild(iconContainer);
          }}
          {...props}
        />
      </>
    );
  }
  return (
    <div
      className={`flex flex-col items-center justify-center text-foreground ${!isImage && ` opacity-50 ${fileIconContainerClassName}`} ${className} cursor-pointer`}
      onClick={handleFileClick}
      {...props}
    >
      {icon}
      {!isImage && showName && file?.name && <span className="text-sm text-center mt-2 px-2 break-words max-w-full">{file.name}</span>}
    </div>
  );
};

const getFileURLAfterPresignedCheck = async ({file, isSwivuUnAssignedFile = false}: {file: FormattedFile; isSwivuUnAssignedFile?: boolean}): Promise<string | null> => {
  try {
    if (file.isCCImage) {
      return file.originalImageUrl || "";
    } else {
      const response = isSwivuUnAssignedFile
        ? await getPresignedImageUrlForUnassignedFile(file.id)
        : await getPresignedImageUrlForFile(file.id);
      return response.data.url;
    }
  } catch (error) {
    console.error("[getFileURLAfterPresignedCheck] Something went wrong when getting file presigned URL", error);
    return null;
  }
}

type SwivuPDFPreviewWrapperProps = PdfViewerProps & {
  previewFileObject: FormattedFile;
  setLoadingState: (loading: boolean) => void;
  isSwivuUnAssignedFile?: boolean;
};

const SwivuPDFPreviewWrapper = ({ previewFileObject, setLoadingState, isSwivuUnAssignedFile = false, ...props }: SwivuPDFPreviewWrapperProps) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
   const handleFileURL = async () => {
    await getFileURLAfterPresignedCheck({file: previewFileObject, isSwivuUnAssignedFile}).then((url) => {
      setFileUrl(url);
    }).catch(() => {
      setLoadingState(false);
      setFileUrl(null);
    });
   }
   handleFileURL();
  }, [previewFileObject, setLoadingState, isSwivuUnAssignedFile]);

  if (!fileUrl) return null;
  return <PdfViewer {...props} fileUrl={fileUrl} />
};

type SwivuWordPreviewWrapperProps = WordViewerProps & {
  previewFileObject: FormattedFile;
  setLoadingState: (loading: boolean) => void;
  isSwivuUnAssignedFile?: boolean;
};

const SwivuWordPreviewWrapper = ({ previewFileObject, isSwivuUnAssignedFile, setLoadingState, ...props }: SwivuWordPreviewWrapperProps) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);

  useEffect(() => {
   const handleFileURL = async () => {
    await getFileURLAfterPresignedCheck({file: previewFileObject, isSwivuUnAssignedFile}).then((url) => {
      setFileUrl(url);
    }).catch(() => {
      setLoadingState(false);
      setFileUrl(null);
    });
   }
   handleFileURL();
  }, [previewFileObject, setLoadingState, isSwivuUnAssignedFile]);

  if (!fileUrl) return null;
  return <WordViewer {...props} fileUrl={fileUrl} />
};

export default FilePreview;
